import { useEffect, useRef, useState } from 'react'
import type { User } from '../types'

// AccountMenu is the signed-in account's button in the top bar. Sign out is in
// its menu: beside the Console link it was one stray click away.
export function AccountMenu({ user }: { user: User }) {
  const [open, setOpen] = useState(false)
  const button = useRef<HTMLButtonElement>(null)
  const menu = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    menu.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()
    const close = (e: MouseEvent) => {
      if (!menu.current?.contains(e.target as Node) && !button.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Escape') return
    setOpen(false)
    button.current?.focus()
  }

  return (
    <div className="relative" onKeyDown={onKeyDown}>
      <button
        ref={button}
        type="button"
        aria-label={user.name}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="h-9 pl-1 pr-2 flex items-center gap-2 rounded-full border border-zinc-800 bg-zinc-900 text-sm text-zinc-100 hover:border-zinc-700 hover:bg-zinc-800 transition-colors"
      >
        {user.picture ? (
          <img src={user.picture} alt="" width={28} height={28} className="w-7 h-7 rounded-full" />
        ) : (
          <span aria-hidden="true" className="w-7 h-7 rounded-full bg-orange-500/15 text-orange-400 flex items-center justify-center text-xs font-bold">
            {initials(user.name)}
          </span>
        )}
        <span className="hidden sm:inline max-w-40 truncate">{user.name}</span>
        <svg aria-hidden="true" className="w-3.5 h-3.5 text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div
          ref={menu}
          role="menu"
          aria-label={user.name}
          className="absolute right-0 mt-2 w-60 z-50 rounded-lg border border-zinc-700 bg-zinc-900 shadow-xl p-1 origin-top-right transition-[opacity,scale] duration-150 ease-out starting:opacity-0 starting:scale-97 motion-reduce:transition-none"
        >
          <div className="px-3 py-2 border-b border-zinc-800 mb-1">
            <div className="text-sm font-semibold text-zinc-100 truncate">{user.name}</div>
            <div className="text-xs text-zinc-400 truncate">{user.email}</div>
          </div>
          <a role="menuitem" href="/auth/logout" className="flex h-9 items-center px-3 rounded-md text-sm text-zinc-200 hover:bg-zinc-800">
            Sign out
          </a>
        </div>
      )}
    </div>
  )
}

// First and last word, by grapheme, so "Jo" is "J" and a name starting with
// an accented letter keeps it whole.
function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  const first = (w: string | undefined) => (w ? [...new Intl.Segmenter().segment(w)][0]?.segment ?? '' : '')
  return (first(words[0]) + (words.length > 1 ? first(words[words.length - 1]) : '')).toUpperCase()
}
